import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  CONFIG_STRIP_FEATURE,
  getValueFromConfigState,
  selectConfigurationsState,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import { createSelector } from '@ngrx/store';

import {
  ConfigStripStateConfigurationKeys,
  ConfigStripStateConfigurations,
  FEATURE_NAME,
} from '../../configurations';
import { SalesUnitCondition } from '../models/sales-unit-condition.model';

export const loadCombinedCallPrices = createSelector(
  selectConfigurationsState,
  (config) => {
    return getValueFromConfigState<
      ConfigStripConfigurationKeys.LoadCombinedCallPrices,
      ConfigStripConfigurations
    >(
      config,
      CONFIG_STRIP_FEATURE,
      ConfigStripConfigurationKeys.LoadCombinedCallPrices,
      configStripConfigurationDefaultValues[
        ConfigStripConfigurationKeys.LoadCombinedCallPrices
      ],
    );
  },
);

export const selectConfigStripIsInStoreDateEnabled = createSelector(
  selectConfigurationValueFrom<
    ConfigStripStateConfigurationKeys.InStoreDateEnabled,
    ConfigStripStateConfigurations
  >(FEATURE_NAME, ConfigStripStateConfigurationKeys.InStoreDateEnabled, false),
  (inStoreDateEnabled) => inStoreDateEnabled,
);

export const selectConfigStripHideOnlyNowLogisticEnabled = createSelector(
  selectConfigurationValueFrom<
    ConfigStripConfigurationKeys.HideOnlyNowInventoryLogistic,
    ConfigStripConfigurations
  >(
    CONFIG_STRIP_FEATURE,
    ConfigStripConfigurationKeys.HideOnlyNowInventoryLogistic,
    configStripConfigurationDefaultValues[
      ConfigStripConfigurationKeys.HideOnlyNowInventoryLogistic
    ],
  ),
  (hideEnabled) => !!hideEnabled,
);

export const selectDisableOnBasketUpdateRule = createSelector(
  selectConfigurationValueFrom<
    ConfigStripConfigurationKeys.DisableOnBasketUpdate,
    ConfigStripConfigurations
  >(
    CONFIG_STRIP_FEATURE,
    ConfigStripConfigurationKeys.DisableOnBasketUpdate,
    configStripConfigurationDefaultValues[
      ConfigStripConfigurationKeys.DisableOnBasketUpdate
    ],
  ),
  (disableOnBasketUpdate) => {
    return disableOnBasketUpdate;
  },
);

export const selectShowTotalPriceRule = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.ShowTotalPriceInConfigStripButton,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.ShowTotalPriceInConfigStripButton,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.ShowTotalPriceInConfigStripButton
  ],
);

export const selectMaxUnlimitedQuantityOrder = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.MaxUnlimitedQuantityOrder,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.MaxUnlimitedQuantityOrder,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.MaxUnlimitedQuantityOrder
  ],
);

export const selectPrioritizeMaxUnlimitedQuantityShown =
  selectConfigurationValueFrom<
    ConfigStripConfigurationKeys.PrioritizeMaxUnlimitedQuantityShown,
    ConfigStripConfigurations
  >(
    CONFIG_STRIP_FEATURE,
    ConfigStripConfigurationKeys.PrioritizeMaxUnlimitedQuantityShown,
    configStripConfigurationDefaultValues[
      ConfigStripConfigurationKeys.PrioritizeMaxUnlimitedQuantityShown
    ],
  );

export const selectSplitInventoriesOverviewForSameDateEnabled =
  selectConfigurationValueFrom<
    ConfigStripConfigurationKeys.SplitInventoriesOverviewForSameDateEnabled,
    ConfigStripConfigurations
  >(
    CONFIG_STRIP_FEATURE,
    ConfigStripConfigurationKeys.SplitInventoriesOverviewForSameDateEnabled,
    configStripConfigurationDefaultValues[
      ConfigStripConfigurationKeys.SplitInventoriesOverviewForSameDateEnabled
    ],
  );

export const selectShowInventoryExternalIdAsCustomLabel =
  selectConfigurationValueFrom<
    ConfigStripConfigurationKeys.ShowInventoryExternalIdAsCustomLabel,
    ConfigStripConfigurations
  >(
    CONFIG_STRIP_FEATURE,
    ConfigStripConfigurationKeys.ShowInventoryExternalIdAsCustomLabel,
    configStripConfigurationDefaultValues[
      ConfigStripConfigurationKeys.ShowInventoryExternalIdAsCustomLabel
    ],
  );

export const selectSkipInventoryCheckEnabled = selectConfigurationValueFrom<
  ConfigStripStateConfigurationKeys.SkipInventoryCheck,
  ConfigStripStateConfigurations
>(FEATURE_NAME, ConfigStripStateConfigurationKeys.SkipInventoryCheck, false);

const selectTrueDemandEnabledRule = createSelector(
  selectConfigurationValueFrom<
    ConfigStripStateConfigurationKeys.TrueDemandEnabled,
    ConfigStripStateConfigurations
  >(FEATURE_NAME, ConfigStripStateConfigurationKeys.TrueDemandEnabled, false),
  Boolean,
);

export const selectTrueDemandFeatureEnabled = createSelector(
  selectTrueDemandEnabledRule,
  selectSkipInventoryCheckEnabled,
  (trueDemandEnabled, skipInventoryEnabled) =>
    Boolean(trueDemandEnabled && !skipInventoryEnabled),
);

export const selectSalesUnitConditions = createSelector(
  selectConfigurationValueFrom<
    ConfigStripStateConfigurationKeys.SalesUnit,
    ConfigStripStateConfigurations
  >(FEATURE_NAME, ConfigStripStateConfigurationKeys.SalesUnit, '[]'),
  (config: string) => {
    try {
      const conditions = JSON.parse(config) as SalesUnitCondition[];
      return conditions.filter(
        (c) => c.isEnabled && !!Object.keys(c.filters).length && c.quantity > 0,
      );
    } catch (e) {
      console.error(`Failed to parse sales unit config: ${e}`);
      return [];
    }
  },
);

export const selectHideInventoryOverviews = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.HideInventoryOverviews,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.HideInventoryOverviews,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.HideInventoryOverviews
  ],
);

export const selectImageDownloadWebpDisabled = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.ImageDownloadWebpDisabled,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.ImageDownloadWebpDisabled,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.ImageDownloadWebpDisabled
  ],
);

export const selectInventoryColorMapper = createSelector(
  selectConfigurationValueFrom<
    ConfigStripConfigurationKeys.InventoryColorMapper,
    ConfigStripConfigurations
  >(
    CONFIG_STRIP_FEATURE,
    ConfigStripConfigurationKeys.InventoryColorMapper,
    configStripConfigurationDefaultValues[
      ConfigStripConfigurationKeys.InventoryColorMapper
    ],
  ),
  (config: string) => {
    try {
      const inventoryColorMapping = JSON.parse(config);
      return inventoryColorMapping;
    } catch (e) {
      console.warn(`Failed to parse inventory color mapping config: ${e}`);
      return undefined;
    }
  },
);

export const selectBackOrderEnabledRule = createSelector(
  selectConfigurationValueFrom<
    ConfigStripStateConfigurationKeys.BackOrderEnabled,
    ConfigStripStateConfigurations
  >(FEATURE_NAME, ConfigStripStateConfigurationKeys.BackOrderEnabled, false),
  Boolean,
);

export const selectShowBackOrderFactoryRule = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.ShowBackOrderFactory,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.ShowBackOrderFactory,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.ShowBackOrderFactory
  ],
);

export const selectShowInfinitySign = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.ShowInfinitySign,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.ShowInfinitySign,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.ShowInfinitySign
  ],
);

export const selectMaxDisplayedInventory = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.MaxDisplayedInventory,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.MaxDisplayedInventory,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.MaxDisplayedInventory
  ],
);

export const selectHideInventoryCellTotals = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.HideInventoryCellTotals,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.HideInventoryCellTotals,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.HideInventoryCellTotals
  ],
);

export const selectCellTotalsFilterRule = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.CellTotalsFilter,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.CellTotalsFilter,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.CellTotalsFilter
  ],
);

export const collapsableInventoriesOverviewMinQuantity = createSelector(
  selectConfigurationValueFrom<
    ConfigStripConfigurationKeys.CollapsableInventoriesOverviewMinQuantity,
    ConfigStripConfigurations
  >(
    CONFIG_STRIP_FEATURE,
    ConfigStripConfigurationKeys.CollapsableInventoriesOverviewMinQuantity,
    2,
  ),
  (value) => value ?? 2,
);

export const maxVisibleInventoriesRule = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.MaxVisibleInventories,
  ConfigStripConfigurations
>(CONFIG_STRIP_FEATURE, ConfigStripConfigurationKeys.MaxVisibleInventories, 0);

export const selectAvailabilityRangesRule = createSelector(
  selectConfigurationValueFrom<
    ConfigStripConfigurationKeys.AvailabilityRanges,
    ConfigStripConfigurations
  >(
    CONFIG_STRIP_FEATURE,
    ConfigStripConfigurationKeys.AvailabilityRanges,
    configStripConfigurationDefaultValues[
      ConfigStripConfigurationKeys.AvailabilityRanges
    ],
  ),
  (value) => {
    try {
      return JSON.parse(value as string);
    } catch (e) {
      console.warn(`Failed to parse availability ranges rule: ${e}`);
      return [];
    }
  },
);
